'use client';

/**
 * PermissionDenied — Phase A4 state component.
 *
 * Companion to EmptyState + ErrorState + LoadingSkeleton. Renders when
 * a user lacks the permission for the surface they navigated to (e.g.
 * RoleGuard mismatch on a regulated route, plan-blocked entitlement,
 * facility-scope rejection). Friendlier than a raw 403 — gives the
 * user context + a path forward (back, contact admin, or upgrade).
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A4 (state components family).
 */

import React from 'react';
import { Lock, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface PermissionDeniedProps {
  /** Title — defaults to "You don't have access to this page". */
  title?: string;
  /** Friendly description of the underlying gate. */
  description?: React.ReactNode;
  /** Override the lock icon (e.g. ShieldOff for a sub-class). */
  icon?: LucideIcon;
  /**
   * Optional primary action (e.g. "Request access", "Contact admin").
   * Renders a primary-styled button.
   */
  primaryAction?: { label: string; onClick: () => void };
  /**
   * Optional secondary action (e.g. "Back to overview", "Upgrade plan").
   * Renders an outline-styled button to the right of the primary.
   */
  secondaryAction?: { label: string; onClick: () => void };
  /**
   * Diagnostic info shown in a small monospace block at the bottom
   * (e.g. the role the user has + the role required). Helpful for
   * debugging in dev / for support tickets.
   */
  diagnostics?: { current?: string; required?: string };
  className?: string;
}

export function PermissionDenied({
  title = "You don't have access to this page",
  description,
  icon: Icon = Lock,
  primaryAction,
  secondaryAction,
  diagnostics,
  className,
}: PermissionDeniedProps) {
  return (
    <div
      role="alert"
      className={cn(
        'relative flex flex-col items-center justify-center overflow-hidden rounded-card border border-status-warning-border bg-status-warning-bg px-6 py-10 text-center',
        className,
      )}
      data-permission-denied
    >
      <div className="absolute inset-x-0 top-0 h-px bg-status-warning-border" />
      <div className="flex h-12 w-12 items-center justify-center rounded-control border border-status-warning-border bg-surface-sheet">
        <Icon className="h-6 w-6 text-status-warning-fg" aria-hidden="true" />
      </div>
      <h2 className="mt-4 max-w-xl font-heading text-card-title text-text-heading">{title}</h2>
      {description && (
        <p className="mt-2 max-w-2xl font-body text-body-sm text-text-body">{description}</p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-control bg-statura-green-accessible px-3 py-1.5 text-body font-medium text-text-inverse hover:bg-statura-green-accessible-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
            >
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-control border border-border bg-surface-sheet px-3 py-1.5 text-body font-medium text-text-heading hover:bg-surface-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
      {diagnostics && (diagnostics.current || diagnostics.required) && (
        <div className="mt-4 rounded-control border border-status-warning-border bg-surface-sheet px-3 py-2 text-caption text-text-body">
          {diagnostics.current && (
            <div>
              <span className="font-medium text-text-heading">Current:</span> {diagnostics.current}
            </div>
          )}
          {diagnostics.required && (
            <div>
              <span className="font-medium text-text-heading">Required:</span>{' '}
              {diagnostics.required}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
